import {
  connectRealtime,
  createRealtimeTokenGetter,
  type RealtimeAuthFailure,
  type RealtimeSocket,
} from '@/lib/api-client';
import { tokenStore } from './tokenStore';

/**
 * One shared socket per tab — components subscribe, they never reconnect.
 *
 * ## What lives here and what lives in `@/lib/api-client`
 *
 * The package owns the socket's own survival: the credential is fetched afresh
 * per handshake, a refused one is refreshed rather than re-offered, a
 * server-initiated hang-up is retried with jittered backoff, and after
 * `maxAuthFailures` consecutive refusals the socket stops and says so through
 * `onAuthFailure`. None of that is reproduced below — it used to be, against an
 * older api-client, and the two layers then counted the same refusals and tore
 * down the same socket from opposite directions.
 *
 * What is left here is the part that is genuinely about *this app*:
 *  - one socket shared by every screen, and a **generation** so replacing it
 *    re-attaches every subscription rather than orphaning them on a corpse;
 *  - **reference-counted rooms**, because two components on one screen routinely
 *    ask for the same `branch:` room;
 *  - a **resync** signal, because a screen that patches its cache from events
 *    drifts across a reconnect and Socket.IO replays nothing.
 */

export type { RealtimeAuthFailure };

/**
 * Where the handshake's credential comes from, asked afresh per attempt.
 *
 * Not `tokenStore.get().accessToken` read once at connect, which is what this
 * used to be: Socket.IO reconnects on its own, for ever, with backoff, and
 * re-sent that captured string every time. A manager's tab sitting on a board
 * makes no HTTP requests, so nothing else discovers the expiry either — one
 * token lifetime in and realtime was over for the shift, with a green pill still
 * showing.
 *
 * Keyed on this exact `tokenStore` instance, the same one `lib/api.ts` hands
 * `createHttpClient`, so a socket reconnect and an HTTP 401 that discover the
 * same expiry together share one refresh instead of spending a rotating refresh
 * token twice. `VITE_API_URL` and not `VITE_REALTIME_URL`: the refresh is an
 * HTTP call to `/auth/refresh`, not a socket frame.
 */
const currentToken = createRealtimeTokenGetter({
  baseURL: import.meta.env.VITE_API_URL ?? '/api',
  tokenStore,
});

let socket: RealtimeSocket | null = null;

/**
 * How many live subscribers each joined room has.
 *
 * Room membership is **sticky** — `connectRealtime` replays the whole set on
 * every reconnect — so letting go of a room has to be deliberate, and letting go
 * at the wrong moment is worse than never letting go at all. Two components on
 * one screen routinely ask for the same `branch:` room, and this dashboard
 * unmounts one subscription while another is still mounted on every re-render
 * that changes a room key. If the first unmount emitted a bare `leave`, the
 * second would go quiet while still on screen and nothing would say why.
 *
 * So a room is only actually left when the **last** holder releases it.
 */
const roomRefs = new Map<string, number>();

let authFailure: RealtimeAuthFailure | null = null;

/**
 * Whether this tab has ever had a working socket.
 *
 * The difference between "connected" and "connected *again*" is the whole
 * resync story: a first connect has nothing to resync, every later one does.
 * Module-level rather than per-socket, so a rebuilt socket still counts as a
 * reconnect for the screens that were watching the old one.
 */
let everConnected = false;

/**
 * Bumped whenever the cached socket is replaced, so subscribers re-attach.
 *
 * This is the piece that makes throwing a socket away safe. Components hold
 * their listeners on the socket object `getSocket()` handed them; replacing that
 * object would otherwise orphan every one of them on a corpse, which is a
 * quieter version of the bug being fixed. `useRealtime` reads this through
 * `useSyncExternalStore` and keeps it in its effect deps, so a new socket
 * re-runs every subscription's effect and re-joins every room.
 *
 * Deliberately **not** bumped when the gateway gives up: the give-up is terminal
 * and a bump would send every subscription straight back to `getSocket()` to
 * build another one, which is the retry loop `maxAuthFailures` exists to end.
 * `reconnectRealtime()` is how a human asks for that instead.
 */
let generation = 0;
const generationListeners = new Set<() => void>();
const failureListeners = new Set<(failure: RealtimeAuthFailure | null) => void>();
const reconnectListeners = new Set<() => void>();

function bumpGeneration(): void {
  generation += 1;
  for (const listener of [...generationListeners]) listener();
}

/** The current socket identity. Changes when the socket is replaced. */
export function socketGeneration(): number {
  return generation;
}

/** Subscribe to the socket being replaced. Returns the unsubscribe. */
export function onSocketChange(listener: () => void): () => void {
  generationListeners.add(listener);
  return () => {
    generationListeners.delete(listener);
  };
}

/**
 * Whether realtime is off because this tab's credential was refused for good.
 *
 * Kept so a screen mounting *after* it happened still renders the truth rather
 * than an optimistic "connected".
 */
export function realtimeAuthFailure(): RealtimeAuthFailure | null {
  return authFailure;
}

/** Subscribe to that answer changing. Returns the unsubscribe. */
export function onRealtimeAuthFailure(
  listener: (failure: RealtimeAuthFailure | null) => void,
): () => void {
  failureListeners.add(listener);
  return () => {
    failureListeners.delete(listener);
  };
}

/**
 * Fires on every connect *after* the first — a Wi-Fi blip healed, a backend
 * restart survived, a socket rebuilt after a refusal.
 *
 * A screen that patches its cache from events and never refetches drifts
 * silently across a reconnect: the socket was down, the venue was not, and every
 * event fired in that window is simply gone. Nothing in Socket.IO replays them.
 */
export function onRealtimeReconnect(listener: () => void): () => void {
  reconnectListeners.add(listener);
  return () => {
    reconnectListeners.delete(listener);
  };
}

function announceFailure(failure: RealtimeAuthFailure | null): void {
  if (authFailure === failure) return;
  authFailure = failure;
  for (const listener of [...failureListeners]) listener(failure);
}

/**
 * Drop the socket and everything that describes it.
 *
 * `disconnect()` before the reference is cleared, and in that order: it destroys
 * the socket on its manager, which sets `skipReconnect` and clears the pending
 * backoff timer. Nulling the reference alone would abandon a socket that carries
 * on reconnecting with a dead credential, unreachable and invisible.
 *
 * The room counts go with it: they describe *this* socket's membership, and a
 * room still held at 1 would make the next socket's `retainRooms()` a no-op — it
 * would join nothing and the screen would stay dark.
 */
function teardown(built: RealtimeSocket | null): void {
  built?.removeAllListeners();
  built?.disconnect();
  if (socket === built) socket = null;
  roomRefs.clear();
}

function build(rooms: string[]): RealtimeSocket {
  const built = connectRealtime({
    baseURL: import.meta.env.VITE_REALTIME_URL ?? 'http://localhost:4000',
    getToken: currentToken,
    rooms,
    // A refused room means this screen is not subscribed to anything, which
    // looks exactly like a quiet venue. Say so instead.
    onJoinDenied: (denied) => {
      for (const { room, reason } of denied) {
        console.error(`[realtime] refused room ${room}: ${reason} — this screen will not update`);
      }
    },
    /**
     * The socket has stopped retrying and will never carry another event.
     *
     * Dropping it from the cache in the same breath is not tidying: without it
     * the next `getSocket()` hands the caller this corpse and every screen it
     * serves is dead until the tab is reloaded.
     */
    onAuthFailure: (failure) => {
      if (socket === built) {
        socket = null;
        roomRefs.clear();
      }
      announceFailure(failure);
    },
  });

  built.on('connect', () => {
    if (everConnected) {
      for (const listener of [...reconnectListeners]) listener();
    }
    everConnected = true;
  });

  // A socket is being tried, so whatever the last one concluded is history.
  announceFailure(null);
  return built;
}

export function getSocket(rooms: string[] = []): RealtimeSocket {
  if (!socket) {
    socket = build(rooms);
  } else if (rooms.length) {
    socket.joinRooms(rooms);
  }
  return socket;
}

/** Rooms the shared socket currently holds — used by the dev-time warning. */
export function joinedRooms(): ReadonlySet<string> {
  return socket?.joinedRooms ?? new Set<string>();
}

/** Distinct, non-empty rooms — a caller may repeat one, and must not double-count. */
function distinctRooms(rooms: string[]): string[] {
  return [...new Set(rooms.filter((room) => typeof room === 'string' && room !== ''))];
}

/**
 * Claim a set of rooms for one subscriber, and hand back the shared socket.
 *
 * Only the rooms nobody held yet are passed on to be joined: the rest are
 * already in the socket's sticky set, and re-asking would put a `join` on the
 * wire for every subscription that mounts — nine of them on the kitchen board
 * alone.
 */
export function retainRooms(rooms: string[]): RealtimeSocket {
  const fresh: string[] = [];
  for (const room of distinctRooms(rooms)) {
    const next = (roomRefs.get(room) ?? 0) + 1;
    roomRefs.set(room, next);
    if (next === 1) fresh.push(room);
  }
  return getSocket(fresh);
}

/**
 * Release rooms claimed through {@link retainRooms} — the counterpart the leak
 * was missing.
 *
 * Switching the active branch used to join `branch:B` and keep `branch:A` for
 * the life of the tab, re-asking for it on every reconnect: a manager who looked
 * at three branches over a shift ended up watching all three, and a screen
 * filtered to one of them still repainted on the others' traffic.
 *
 * `leaveRooms`, not a raw `emit('leave')`: it also subtracts the rooms from the
 * sticky set the package replays on reconnect. A bare emit would leave the room
 * now and silently re-join it at the next blip — the leak, with an extra step.
 */
export function releaseRooms(rooms: string[]): void {
  const dead: string[] = [];
  for (const room of distinctRooms(rooms)) {
    const held = roomRefs.get(room);
    if (!held) continue;
    if (held > 1) {
      roomRefs.set(room, held - 1);
      continue;
    }
    roomRefs.delete(room);
    dead.push(room);
  }
  if (dead.length) socket?.leaveRooms(dead);
}

/**
 * Tear the socket down and forget it.
 *
 * Call this wherever the identity behind the handshake changes — sign-out, a
 * refresh the server refused. A socket is authenticated **once, at connect**, so
 * one left running after a sign-out is still presenting the old user's token,
 * and `getSocket()` would hand that same object to whoever signs in next, whose
 * room joins would then be authorized against the previous user's scopes.
 */
export function disconnectSocket(): void {
  teardown(socket);
  everConnected = false;
  announceFailure(null);
  bumpGeneration();
}

/**
 * Try again after realtime gave up — what a "Reconnect" button calls.
 *
 * The give-up is deliberate and terminal on its own; this is the human saying
 * the reason for it has been dealt with (signed back in, network fixed). The
 * generation bump is the working half: it re-runs every subscription's effect,
 * and *those* call `getSocket()` with the rooms they still want. A socket built
 * here would have no rooms and no listeners.
 */
export function reconnectRealtime(): void {
  teardown(socket);
  announceFailure(null);
  bumpGeneration();
}
